import Header from "../../ui/header/Header";
import HeaderSaleBanner from "../../ui/header/HeaderSaleBanner";
import {
    hasAdminGroup,
    hasAssistantGroup,
    hasEffectiveActionCourseSubscriberGroup,
    hasEffectiveActionMiniCourseSubscriberGroup,
    hasExerciseReviewerGroup,
    hasSpcCourseSubscriberGroup,
    hasUnderstandTeenagerCourseSubscriberGroup
} from "../../../util/AuthUtil";
import {useUser} from "../../../context/UserContext";
import {getProductAccessDeadline, shouldLockProducts} from "../../../util/ProductAccessUtil";
import {useRouteLoaderData} from "react-router-dom";
import {User} from "../../../model/user/User";

interface ProductsHeaderProps {
    loaderId: string;
}

export default function ProductsHeader({loaderId}: ProductsHeaderProps) {
    // First try to get user from loader data for immediate rendering
    const loaderUser = useRouteLoaderData(loaderId) as User | null;
    // Fall back to context if loader data not available
    const {user: contextUser, groups: contextGroups} = useUser();

    // Use loader user if available, otherwise fall back to context
    const user = loaderUser || contextUser;
    const groups = user?.groups || contextGroups;

    const isKedSubscriber = hasEffectiveActionCourseSubscriberGroup(groups)
    const isPedSubscriber = hasEffectiveActionMiniCourseSubscriberGroup(groups)
    const isZnSubscriber = hasUnderstandTeenagerCourseSubscriberGroup(groups)
    const isSpcSubscriber = hasSpcCourseSubscriberGroup(groups)

    const isAnySubscriber = isKedSubscriber || isPedSubscriber || isZnSubscriber || isSpcSubscriber

    // Check if parent should see ZN promotional link (for parents without ZN access)
    const shouldShowZnPromoLink = user?.isParent && !isZnSubscriber
    // Check if parent has ZN access (for direct course link)
    const parentHasZnAccess = user?.isParent && isZnSubscriber

    const hasProSubscription = user?.saasSubscription?.isActive;

    // For V1-only users, redirect to old KED course, not V2
    const kedCoursePath = user?.hasAccessToKedV1Only ? '/produkt/kurs-efektywnego-dzialania' : '/produkt/kurs-efektywnego-dzialania-v2';
    const programMenuItem = {
        title: 'Program',
        to: isKedSubscriber ? kedCoursePath
            : isZnSubscriber ? '/produkt/zrozumiec-nastolatka'
            : isSpcSubscriber ? '/produkt/system-planowania-czasu'
            : '/produkt/podstawy-efektywnego-dzialania'
    };

    const communityMenuItem = {
        title: 'Społeczność',
        to: '/produkt/spolecznosc'
    };

    const proPlatform = {
        title: 'Przejdź na pro',
        to: '/produkt/pro'
    };

    const referralsMenuItem = {
        title: 'Polecaj i zarabiaj',
        to: '/produkt/polecenia'
    };

    const parentPanel = {
        title: 'Panel rodzica',
        to: '/produkt/panel-rodzica'
    };

    // Promotional link for parents without ZN access
    const znCoursePromoMenuItem = {
        title: 'Zrozumieć Nastolatka',
        to: '/oferta/zrozumiec-nastolatka'
    };

    // Direct course link for parents with ZN access
    const znCourseAccessMenuItem = {
        title: 'Zrozumieć Nastolatka',
        to: '/produkt/zrozumiec-nastolatka'
    };

    // KED course menu item for parents (named "Kurs Efektywnego Działania" instead of "Program")
    // For V1-only users, redirect to old KED course, not V2
    const kedCourseMenuItem = {
        title: 'Kurs Efektywnego Działania',
        to: kedCoursePath
    };

    // Check if user paid full price (hide referrals for them)
    const paidFullPrice = user?.paidFullPrice === true;

    // Setup topItems array based on user type and subscription status
    let topItems;

    if (user?.isParent) {
        // For parents: Panel Rodzica, ZN (if access), KED (if access), Społeczność
        // NO Narzędzia tab for parents
        topItems = [parentPanel];

        // Add ZN course link if parent has ZN access
        if (parentHasZnAccess) {
            topItems.push(znCourseAccessMenuItem);
        }

        // Add KED course link if parent has KED access
        if (isKedSubscriber) {
            topItems.push(kedCourseMenuItem);
        }

        // Add community
        topItems.push(communityMenuItem);

        // Add referrals (only if user didn't pay full price)
        if (!paidFullPrice) {
            topItems.push(referralsMenuItem);
        }

        // Add ZN promotional link if parent doesn't have ZN access yet
        if (shouldShowZnPromoLink) {
            // TODO: In the future after adjust this page, add it. (Removed from App.tsx as well)
            // topItems.push(znCoursePromoMenuItem);
        }
    } else if (isAnySubscriber) {
        // Non-parent subscribers: Program, Społeczność, Polecaj i zarabiaj
        topItems = [programMenuItem, communityMenuItem];

        // Add referrals (only if user didn't pay full price)
        if (!paidFullPrice) {
            topItems.push(referralsMenuItem);
        }

        // Add Pro platform link if user doesn't have pro subscription
        if (!hasProSubscription) {
            topItems.push(proPlatform);
        }
    } else {
        // No subscription: Społeczność, Polecaj i zarabiaj, Kurs offer
        topItems = [
            communityMenuItem,
        ];

        // Add referrals (only if user didn't pay full price)
        if (!paidFullPrice) {
            topItems.push(referralsMenuItem);
        }

        topItems.push({
            title: 'Kurs',
            to: '/produkt/oferta/kurs-efektywnego-dzialania'
        });

        // Add Pro platform link if user doesn't have pro subscription
        if (!hasProSubscription) {
            topItems.push(proPlatform);
        }
    }

    // Check if products are locked for this user
    const productsLocked = user ? shouldLockProducts(user) : false;

    // Check if user is admin/assistant/reviewer
    const isSpecialUser = user && groups && (
        hasAdminGroup(groups) ||
        hasAssistantGroup(groups) ||
        hasExerciseReviewerGroup(groups)
    );

    // Check if deadline has passed
    const now = new Date();
    const deadlineHasPassed = now >= getProductAccessDeadline();

    // Rankings should only be visible to special users or after the deadline
    const showRankings = isSpecialUser || deadlineHasPassed;

    // Build dropdown items based on access
    const dropdownItems = [
        {
            title: 'Ustawienia',
            to: '/ustawienia'
        }
    ];

    // Only add Statistics if products are not locked
    if (!productsLocked) {
        dropdownItems.push({
            title: 'Statystyki',
            to: '/statystyki'
        });

        // Only add Rankings if user has special access or deadline has passed
        if (showRankings) {
            dropdownItems.push({
                title: 'Rankingi',
                to: '/ranking'
            });
        }
    }

    return (
        <>
            <HeaderSaleBanner/>
            <Header
                loaderId={loaderId}
                topItems={topItems}
                logoCustomRedirect='/produkt'
                dropdownItems={dropdownItems}
                useDarkTheme={true}
            />
        </>
    );
}
